import { Entitlements } from '@trello/entitlements';

import { getMembership } from '../membership/getMembership';
import {
  getMembershipTypeFromOrganization,
  isAdminOfOrganization,
} from '../organization';
import type { MemberType } from './memberType.types';

/**
 * Returns board admins. Has an `implicitAdmin` option that lets you decide whether you want to count
 * premium and enterprise workspace admins as board admins.
 * Disable implicitAdmin if you don't want enterprise admins and workspace
 * admins to be automatically counted as board admins if workspace is premium or enterprise.
 */
export const getMemberTypeFromBoard = (
  member: {
    id: string;
    idPremOrgsAdmin: Array<string>;
    memberType: 'ghost' | 'normal';
  } & {
    idEnterprisesAdmin?: string[] | null;
  },
  board: {
    memberships: {
      deactivated: boolean;
      idMember: string;
      memberType: 'admin' | 'normal' | 'observer';
      unconfirmed: boolean;
    }[];
  },
  organization:
    | ({ id: string; offering: string } & {
        memberships: ({
          deactivated: boolean;
          idMember: string;
          memberType: 'admin' | 'normal';
          unconfirmed: boolean;
        } & {
          id?: string;
        })[];
        enterprise?: {
          id?: string;
          idAdmins?: Array<string>;
        } | null;
      })
    | null
    | undefined,
  implicitAdmin = true,
): MemberType => {
  if (member.memberType === 'ghost') {
    return 'pending';
  }

  // If the member is an admin of the board's workspace's enterprise, they are
  // implicitly an admin of the board
  if (
    implicitAdmin &&
    organization?.enterprise?.idAdmins?.includes(member.id)
  ) {
    return 'admin';
  }

  // If the member is an admin of the board's workspace, they are implicitly an
  // admin of the board if the workspace is premium
  if (
    implicitAdmin &&
    organization &&
    !Entitlements.isFree(organization.offering) &&
    isAdminOfOrganization(member, organization)
  ) {
    return 'admin';
  }

  const boardMembership = getMembership(board.memberships, member.id);

  if (boardMembership) {
    if (boardMembership.deactivated) {
      return 'deactivated';
    }

    if (boardMembership.unconfirmed) {
      return 'unconfirmed';
    }

    return boardMembership.memberType;
  }

  // Converted from app/scripts/models/internal/MembershipModel.ts getExplicitMemberType
  if (organization && getMembershipTypeFromOrganization(member, organization)) {
    return 'org';
  }

  return 'public';
};
